import { track } from "~/modules/analytics/track";
import {
    FilterControlsEvents,
    FilterFormEvents,
    FilterListEvents,
    InlineFilterEvents,
} from "../analytics/events/filters";
import { FilterOperator } from "./types";

// // // //
// Inline Filters Analytics

/**
 * trackClearInlineFilters
 * Tracks the event when a user clears the applied inline filters
 */
export function trackInlineClearFilters() {
    track(InlineFilterEvents.ClearFilters);
}

/**
 * trackInlineSearch
 * Tracks the event when a new user updates the search field
 */
export function trackInlineSearch(props: { value: string }) {
    track(InlineFilterEvents.Search, props);
}

/**
 * trackInlineMultiSelectSelectAll
 * Tracks the event when a new user clicks the "Select All" button in a Multiselect
 */
export function trackInlineMultiSelectSelectAll(props: { field: string }) {
    track(InlineFilterEvents.MultiSelectSelectAll, props);
}

/**
 * trackInlineMultiSelectDeselectAll
 * Tracks the event when a new user clicks the "Select All" button in a Multiselect
 */
export function trackInlineMultiSelectDeselectAll(props: { field: string }) {
    track(InlineFilterEvents.MultiSelectDeselectAll, props);
}

/**
 * trackInlineMultiSelectValueSelected
 * Tracks the event when a new user selects a single value in a Multiselect
 */
export function trackInlineMultiSelectValueSelected(props: {
    field: string;
    value: string | number;
}) {
    track(InlineFilterEvents.MultiSelectSelectValue, props);
}

/**
 * trackInlineMultiSelectValueDeselected
 * Tracks the event when a new user deselects a single value in a Multiselect
 */
export function trackInlineMultiSelectValueDeselected(props: {
    field: string;
    value: string | number;
}) {
    track(InlineFilterEvents.MultiSelectDeselectValue, props);
}

// // // //
// Pro Search - FilterForm Analytics

/**
 * trackFilterFormChangeField
 * Tracks the event when a user changes the field in the filter form
 * @param prevField - The previous field value
 * @param nextField - The next field value
 */
export function trackFilterFormChangeField(props: {
    prevField: string;
    nextField: string;
}) {
    track(FilterFormEvents.ChangeField, props);
}

/**
 * trackFilterFormChangeOperator
 * Tracks the event when a user changes the operator in the filter form
 * @param prevOperator - The previous operator value
 * @param nextOperator - The next operator value
 */
export function trackFilterFormChangeOperator(props: {
    prevOperator: FilterOperator;
    nextOperator: FilterOperator;
}) {
    track(FilterFormEvents.ChangeOperator, props);
}

/**
 * trackFilterFormChangeValue
 * Tracks the event when a user changes the value in the filter form
 * @param prevValue - The previous value
 * @param nextValue - The next value
 */
export function trackFilterFormChangeValue(props: {
    prevValue: any;
    nextValue: any;
}) {
    track(FilterFormEvents.ChangeValue, props);
}

/**
 * trackFilterFormClickClone
 * Tracks the event when a user clicks the "Clone" button in the filter form
 */
export function trackFilterFormClickClone(props: {
    field: string;
    operator: FilterOperator;
    value: any;
}) {
    track(FilterFormEvents.ClickClone, props);
}

/**
 * trackFilterFormClickDelete
 * Tracks the event when a user clicks the "Delete" button in the filter form
 */
export function trackFilterFormClickDelete(props: {
    field: string;
    operator: FilterOperator;
    value: any;
}) {
    track(FilterFormEvents.ClickDelete, props);
}

// // // //
// Pro Search - FilterList Analytics

/**
 * trackFilterListAddGroup
 * Tracks the event when a user clicks the "Add Group" button in FilterList
 */
export function trackFilterListAddGroup() {
    track(FilterListEvents.AddGroup);
}

/**
 * trackFilterListAddCondition
 * Tracks the event when a user clicks the "Add Condition" button in FilterList
 */
export function trackFilterListAddCondition() {
    track(FilterListEvents.AddCondition);
}

/**
 * trackFilterListRemoveGroup
 * Tracks the event when a user clicks the "Remove All" button in FilterList
 */
export function trackFilterListRemoveGroup() {
    track(FilterListEvents.RemoveGroup);
}

/**
 * trackFilterListToggleAndOr
 * Tracks the event when a user toggles the "And / Or" option in FilterList
 * @param prevOperator - The previous operator value ("And" or "Or")
 * @param nextOperator - The next operator value ("And" or "Or")
 */
export function trackFilterListToggleAndOr(props: {
    prevOperator: FilterOperator;
    nextOperator: FilterOperator;
}) {
    track(FilterListEvents.ToggleAndOr, props);
}

/**
 * trackFilterListDragDropReorder
 * Tracks the event when a user performs drag/drop/reorder operation in FilterList
 */
export function trackFilterListDragDropReorder() {
    track(FilterListEvents.DragDropReorder);
}

// // // //
// Pro Search - FilterControls Analytics

/**
 * trackApplySearch
 * Tracks the event when a user applies a search in FilterControls
 */
export function trackApplySearch() {
    track(FilterControlsEvents.ApplySearch);
}

/**
 * trackDiscardSearch
 * Tracks the event when a user discards a search in FilterControls
 */
export function trackDiscardSearch() {
    track(FilterControlsEvents.DiscardSearch);
}
